import React, { useEffect, useState } from "react";
import axios from "axios";
import { Settings2, Save, RotateCcw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_API_URL || "";

function hdr() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function CommentPolicyPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [policy, setPolicy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    axios.get(`${API}/api/comment-policy`, { headers: hdr() })
      .then(r => setPolicy(r.data))
      .catch(() => setMsg({ kind: "err", text: "Failed to load policy" }))
      .finally(() => setLoading(false));
  }, []);

  async function save() {
    if (!policy) return;
    setSaving(true); setMsg(null);
    try {
      const r = await axios.put(`${API}/api/comment-policy`, {
        enforcementLevel:   policy.enforcementLevel,
        thresholdMinutes:   Number(policy.thresholdMinutes),
        hardRequireMinutes: Number(policy.hardRequireMinutes),
        minLength:          Number(policy.minLength),
      }, { headers: hdr() });
      setPolicy(r.data);
      setMsg({ kind: "ok", text: "Policy saved" });
    } catch (e: any) {
      setMsg({ kind: "err", text: e?.response?.data?.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setPolicy({ ...policy, enforcementLevel: "SMART", thresholdMinutes: 30, hardRequireMinutes: 120, minLength: 20 });
  }

  if (loading) return <div className="p-6 text-gray-500">Loading policy…</div>;
  if (!policy)  return <div className="p-6 text-red-500">No policy available</div>;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-6">
        <Settings2 size={22} className="text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">Time Log Comment Policy</h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Control when comments are required on time logs. Changes apply to new logs only.
      </p>

      {/* Enforcement Level */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Enforcement Level</h2>
        <div className="space-y-2">
          {[
            { v: "OFF",    label: "Off",    desc: "No comment requirements" },
            { v: "SMART",  label: "Smart",  desc: "Required only for longer durations" },
            { v: "STRICT", label: "Strict", desc: "Comment required on every log" },
          ].map(opt => (
            <label key={opt.v} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${policy.enforcementLevel === opt.v ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"} ${!isAdmin ? "opacity-60 cursor-not-allowed" : ""}`}>
              <input
                type="radio" name="enforcement" value={opt.v}
                checked={policy.enforcementLevel === opt.v}
                disabled={!isAdmin}
                onChange={e => setPolicy({ ...policy, enforcementLevel: e.target.value })}
                className="mt-1"
              />
              <div>
                <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                <p className="text-xs text-gray-500">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Thresholds */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Thresholds</h2>

        <div className="mb-4">
          <label className="block text-sm text-gray-700 mb-1">
            Recommend comment after (minutes)
          </label>
          <input
            type="number" min={15} max={120}
            value={policy.thresholdMinutes}
            disabled={!isAdmin}
            onChange={e => setPolicy({ ...policy, thresholdMinutes: e.target.value })}
            className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">Range: 15–120</p>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-700 mb-1">
            Require comment after (minutes)
          </label>
          <input
            type="number" min={60} max={480}
            value={policy.hardRequireMinutes}
            disabled={!isAdmin}
            onChange={e => setPolicy({ ...policy, hardRequireMinutes: e.target.value })}
            className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">Range: 60–480. Must be greater than "recommend after".</p>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Minimum comment length (characters)
          </label>
          <input
            type="number" min={10} max={200}
            value={policy.minLength}
            disabled={!isAdmin}
            onChange={e => setPolicy({ ...policy, minLength: e.target.value })}
            className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">Range: 10–200. Applies when comment is required.</p>
        </div>
      </section>

      {msg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${msg.kind === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {msg.text}
        </div>
      )}

      {isAdmin && (
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60">
            <Save size={16}/> {saving ? "Saving…" : "Save Policy"}
          </button>
          <button onClick={reset}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            <RotateCcw size={16}/> Reset to Defaults
          </button>
        </div>
      )}

      {!isAdmin && (
        <p className="text-xs text-gray-400 italic">Read-only — admins can edit this policy.</p>
      )}
    </div>
  );
}
